import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function ResourcesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const resourcesStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "AI Marketing Resources for Wisconsin Businesses | Technelle",
    "description": "Free AI marketing guides, articles, and insights for Wisconsin businesses. Learn how to leverage AI for marketing automation, demand generation, and business growth.",
    "url": "https://technelle.ai/resources",
    "publisher": {
      "@type": "Organization",
      "name": "Technelle",
      "url": "https://technelle.ai"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "AI Marketing Resources",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "10 AI Marketing Strategies That Drive Real ROI — Free Guide",
          "url": "https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/f101d0b5-ac2f-4206-95fc-3c290c8bf204_technelle_ai_marketing_guide.pdf?v=5a533806b2a50757bc66a9847c1790bf",
          "description": "The practical guide Wisconsin SMBs are using to cut marketing waste and grow revenue with AI."
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "How Wisconsin Businesses Use AI Chatbots to Transform Lead Generation",
          "url": "https://technelle.ai/resources",
          "description": "Discover how Wisconsin businesses are using AI-powered chatbots to capture and qualify leads 24/7."
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "The Complete Guide to Account-Based Marketing for Wisconsin B2B Companies",
          "url": "https://technelle.ai/resources",
          "description": "Learn how Wisconsin businesses implement winning ABM strategies that target ideal customers."
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "5 Ways AI Is Revolutionizing Content Marketing for Wisconsin Businesses",
          "url": "https://technelle.ai/resources",
          "description": "From ideation to optimization, see how Wisconsin marketers use AI tools to create better content faster."
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="AI Marketing Resources for Wisconsin Businesses | Technelle"
        description="Free AI marketing guides, articles, and insights for Wisconsin businesses. Learn how to leverage AI for marketing automation, demand generation, and business growth. Expert resources from Waukesha's leading marketing consultancy."
        keywords="AI marketing resources, Wisconsin business guides, marketing automation guides, AI implementation resources, Wisconsin marketing insights, business growth resources, marketing strategy guides"
        canonical="https://technelle.ai/resources"
        ogTitle="AI Marketing Resources for Wisconsin Businesses | Technelle"
        ogDescription="Expert guides and insights on AI marketing, automation, and growth strategies for Wisconsin businesses."
        ogUrl="https://technelle.ai/resources"
        structuredData={resourcesStructuredData}
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Resources', url: 'https://technelle.ai/resources' }
        ]}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-10" />
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/#about" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Services</Link>
            <Link to="/#case-studies" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Case Studies</Link>
            <Link to="/resources" className="text-[16px] text-[#14B8A6] font-medium transition-colors">Resources</Link>
            <Link to="/how-we-work" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">How We Work</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/#contact" className="hidden lg:block bg-[#14B8A6] text-white px-6 py-2.5 rounded-full text-[16px] font-semibold hover:bg-[#0F9688] transition-colors whitespace-nowrap shadow-lg">
              Book Free Strategy Call
            </Link>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-[#1a1a1a] cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
              <span className="text-[#1a1a1a] font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-[#1a1a1a] cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Services</Link>
              <Link to="/resources" onClick={() => setMobileNavOpen(false)} className="text-[#14B8A6] text-[16px] font-medium">Resources</Link>
              <Link to="/how-we-work" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">How We Work</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <Link
                to="/#contact"
                onClick={() => setMobileNavOpen(false)}
                className="block w-full text-center bg-[#14B8A6] text-white py-4 rounded-full text-[15px] font-semibold hover:bg-[#0F9688] transition-colors"
              >
                Book Free Strategy Call
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#f8f8f8] to-white pt-40 pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <span className="text-[14px] uppercase tracking-wider text-[#14B8A6] font-semibold">Knowledge Hub</span>
          <h1 className="text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.1] font-bold text-[#1a1a1a] mt-6 mb-8">
            AI & Marketing Insights
          </h1>
          <p className="text-[18px] sm:text-[22px] text-[#666666] max-w-3xl mx-auto leading-relaxed">
            Expert guidance on implementing AI, scaling marketing, and driving operational efficiency for growing Wisconsin businesses.
          </p>
        </div>
      </section>

      {/* Featured Guide */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Guide Cover — matches homepage exactly */}
            <div className="flex justify-center">
              <div className="w-full max-w-sm mx-auto">
                <div
                  className="relative rounded-2xl overflow-hidden flex flex-col justify-between"
                  style={{ aspectRatio: '3/4', background: 'linear-gradient(160deg, #0d1b2a 0%, #112240 60%, #0d1b2a 100%)' }}
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#2196F3]"></div>
                  <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#2196F3 1px, transparent 1px), linear-gradient(90deg, #2196F3 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
                  <div className="relative z-10 p-8 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-8">
                      <div className="w-6 h-6 bg-[#2196F3] rounded-sm flex items-center justify-center">
                        <i className="ri-flashlight-line text-white text-xs"></i>
                      </div>
                      <span className="text-[11px] uppercase tracking-widest text-[#2196F3] font-bold">Technelle</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-[#2196F3]/20 border border-[#2196F3]/40 rounded-full px-3 py-1 mb-6 self-start">
                      <i className="ri-download-line text-[#2196F3] text-xs"></i>
                      <span className="text-[10px] uppercase tracking-widest text-[#2196F3] font-bold">Free Guide</span>
                    </div>
                    <h3 className="text-[24px] font-bold text-white leading-tight mb-3">
                      10 AI Marketing Strategies That Drive Real ROI
                    </h3>
                    <p className="text-[13px] text-white/60 leading-relaxed mb-auto">
                      For Wisconsin Small &amp; Mid-Sized Businesses
                    </p>
                    <div className="w-12 h-0.5 bg-[#2196F3] my-6"></div>
                    <div className="border-t border-white/10 pt-5">
                      <div className="text-[10px] text-white/35 uppercase tracking-wider mb-2">Author</div>
                      <div className="text-[15px] font-semibold text-white">Melissa Thomas</div>
                      <div className="text-[12px] text-[#2196F3] mt-0.5">Technelle &nbsp;·&nbsp; Waukesha, Wisconsin</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Guide Info */}
            <div>
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Free Download</div>
              <h2 className="text-[36px] font-bold text-white mb-6 leading-tight">
                Free Guide: 10 AI Marketing Strategies for Wisconsin Businesses
              </h2>
              <p className="text-[17px] text-white/90 leading-relaxed mb-6">
                The practical guide Wisconsin SMBs are using to cut marketing waste and grow revenue with AI — no technical background required.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0"></i>
                  <span className="text-[15px] text-white/90">Why AI is your single biggest competitive advantage right now</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0"></i>
                  <span className="text-[15px] text-white/90">The 10 strategies generating measurable ROI for local businesses</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0"></i>
                  <span className="text-[15px] text-white/90">How to outrank competitors without doubling your ad budget</span>
                </li>
              </ul>
              <a
                href="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/f101d0b5-ac2f-4206-95fc-3c290c8bf204_technelle_ai_marketing_guide.pdf?v=5a533806b2a50757bc66a9847c1790bf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-8 py-4 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
              >
                <i className="ri-download-line text-lg"></i>
                Download the Free Guide
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Insights */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Latest Insights for Wisconsin Businesses</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img
                src="https://public.readdy.ai/ai/img_res/0adffd2ea05bf8e090b20555d572f2b1.jpg"
                alt="AI chatbot marketing automation for lead generation - how to implement conversational AI for business growth"
                className="w-full h-48 object-cover"
                loading="lazy"
                decoding="async"
                width="600"
                height="400"
              />
              <div className="p-6">
                <div className="text-sm text-blue-600 font-semibold mb-2">AI IMPLEMENTATION</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How Wisconsin Businesses Use AI Chatbots to Transform Lead Generation</h3>
                <p className="text-gray-600 mb-4">Discover how Wisconsin businesses are using AI-powered chatbots to capture and qualify leads 24/7, increasing conversion rates by up to 40%.</p>
                <a href="#" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center whitespace-nowrap">
                  Read More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </article>

            <article className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img
                src="https://readdy.ai/api/search-image?query=Business%20professional%20analyzing%20marketing%20data%20analytics%20dashboard%20on%20multiple%20screens%20showing%20customer%20journey%20metrics%20conversion%20funnels%20and%20ROI%20charts%20in%20modern%20office%20representing%20account-based%20marketing%20strategy&width=600&height=400&seq=article-abm-001&orientation=landscape"
                alt="Account-based marketing ABM strategy guide - B2B marketing tactics for targeting high-value accounts Wisconsin"
                className="w-full h-48 object-cover"
                loading="lazy"
                decoding="async"
                width="600"
                height="400"
              />
              <div className="p-6">
                <div className="text-sm text-blue-600 font-semibold mb-2">STRATEGY</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">The Complete Guide to Account-Based Marketing for Wisconsin B2B Companies</h3>
                <p className="text-gray-600 mb-4">Learn how Wisconsin businesses implement winning ABM strategies that target ideal customers with personalized campaigns at scale.</p>
                <a href="#" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center whitespace-nowrap">
                  Read More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </article>

            <article className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <img
                src="https://readdy.ai/api/search-image?query=Marketing%20team%20collaborating%20on%20content%20strategy%20with%20AI%20writing%20tools%20on%20laptop%20showing%20SEO%20optimization%20and%20content%20calendar%20in%20creative%20workspace%20representing%20AI-powered%20content%20marketing%20and%20automation&width=600&height=400&seq=article-content-001&orientation=landscape"
                alt="AI content marketing tools and SEO optimization strategies - how to scale content creation with artificial intelligence"
                className="w-full h-48 object-cover"
                loading="lazy"
                decoding="async"
                width="600"
                height="400"
              />
              <div className="p-6">
                <div className="text-sm text-blue-600 font-semibold mb-2">CONTENT MARKETING</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">5 Ways AI Is Revolutionizing Content Marketing for Wisconsin Businesses</h3>
                <p className="text-gray-600 mb-4">From ideation to optimization, see how Wisconsin marketers use AI tools to create better content faster while maintaining quality and authenticity.</p>
                <a href="#" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center whitespace-nowrap">
                  Read More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="bg-[#1a1a1a] rounded-3xl p-8 sm:p-16 text-center">
            <h2 className="text-[32px] sm:text-[44px] font-bold text-white mb-6">Get Weekly Growth Insights for Wisconsin Businesses</h2>
            <p className="text-[18px] sm:text-[20px] text-[#cccccc] mb-10 max-w-2xl mx-auto leading-relaxed">
              Join 2,500+ Wisconsin business owners receiving actionable AI and marketing strategies every Tuesday.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" onSubmit={(e) => e.preventDefault()}>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                aria-label="Email address for newsletter subscription"
                className="flex-1 bg-white text-[#1a1a1a] px-6 py-4 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
              />
              <button type="submit" className="bg-[#14B8A6] text-white px-8 py-4 rounded-lg text-[16px] font-semibold hover:bg-[#0F9688] transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </form>
            <p className="text-[14px] text-[#999999] mt-4">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-12">
            <span className="text-[13px] uppercase tracking-wider text-[#14B8A6] font-semibold">You Might Also Like</span>
            <h2 className="text-[32px] sm:text-[38px] font-bold text-[#1a1a1a] mt-3 mb-3 leading-tight">
              Keep Exploring
            </h2>
            <p className="text-[17px] text-[#666666] max-w-xl leading-relaxed">
              More ways to understand how Technelle works and what results look like for Wisconsin businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* FAQ */}
            <Link
              to="/faq"
              className="group bg-gray-50 rounded-xl p-8 border border-gray-100 hover:border-[#14B8A6]/40 transition-all hover:-translate-y-0.5"
            >
              <div className="w-12 h-12 bg-[#14B8A6]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#14B8A6]/20 transition-colors">
                <i className="ri-question-answer-line text-[#14B8A6] text-xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-[#14B8A6] font-semibold mb-2">FAQ</div>
              <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#14B8A6] transition-colors leading-snug">
                Common Questions, Straight Answers
              </h3>
              <p className="text-[14px] text-[#666666] leading-relaxed mb-5">
                How long does a project take? What kind of ROI can I expect? What industries do you work with? Get the answers Wisconsin business owners ask most.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[13px] text-[#14B8A6] font-semibold">
                Read the FAQ <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>

            {/* How We Work */}
            <Link
              to="/how-we-work"
              className="group bg-gray-50 rounded-xl p-8 border border-gray-100 hover:border-[#14B8A6]/40 transition-all hover:-translate-y-0.5"
            >
              <div className="w-12 h-12 bg-[#14B8A6]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#14B8A6]/20 transition-colors">
                <i className="ri-settings-3-line text-[#14B8A6] text-xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-[#14B8A6] font-semibold mb-2">Process</div>
              <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#14B8A6] transition-colors leading-snug">
                How Technelle Actually Works
              </h3>
              <p className="text-[14px] text-[#666666] leading-relaxed mb-5">
                From the first strategy call to ongoing optimization — see the exact process Technelle uses to build AI marketing systems that compound over time.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[13px] text-[#14B8A6] font-semibold">
                See the process <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>

            {/* Case Studies */}
            <Link
              to="/case-studies"
              className="group bg-gray-50 rounded-xl p-8 border border-gray-100 hover:border-[#14B8A6]/40 transition-all hover:-translate-y-0.5"
            >
              <div className="w-12 h-12 bg-[#14B8A6]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#14B8A6]/20 transition-colors">
                <i className="ri-bar-chart-grouped-line text-[#14B8A6] text-xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-[#14B8A6] font-semibold mb-2">Case Studies</div>
              <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#14B8A6] transition-colors leading-snug">
                Real Results for Real Wisconsin Businesses
              </h3>
              <p className="text-[14px] text-[#666666] leading-relaxed mb-5">
                206% traffic growth. A 300-person national waitlist with zero ad spend. A new HVAC brand built from scratch. See the numbers behind the work.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[13px] text-[#14B8A6] font-semibold">
                View case studies <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#f8f8f8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[36px] sm:text-[48px] font-bold text-[#1a1a1a] mb-6">Ready to Implement These Strategies for Your Wisconsin Business?</h2>
          <p className="text-[20px] text-[#666666] mb-10 max-w-2xl mx-auto leading-relaxed">
            Book a free consultation and we'll create a custom roadmap tailored to your Wisconsin business goals.
          </p>
          <Link to="/#contact" className="bg-[#14B8A6] text-white px-12 py-5 rounded-lg text-[18px] font-semibold hover:bg-[#0F9688] transition-all inline-block whitespace-nowrap shadow-lg">
            Schedule Your Free Strategy Call
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white rounded-t-[32px] py-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-4 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-5" loading="lazy" decoding="async" width="80" height="20" />
                <span className="text-[20px] font-bold text-white">Technelle</span>
              </div>
              <p className="text-[16px] text-[#999999]">AI-Driven Marketing & Operational Excellence</p>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6 font-semibold">Quick Links</div>
              <nav className="space-y-4">
                <Link to="/#about" className="block text-white hover:text-[#14B8A6] transition-colors">About Us</Link>
                <Link to="/services" className="block text-white hover:text-[#14B8A6] transition-colors">Services</Link>
                <Link to="/#case-studies" className="block text-white hover:text-[#14B8A6] transition-colors">Case Studies</Link>
                <Link to="/resources" className="block text-white hover:text-[#14B8A6] transition-colors">Resources</Link>
                <Link to="/how-we-work" className="block text-white hover:text-[#14B8A6] transition-colors">How We Work</Link>
              </nav>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6 font-semibold">Get Started</div>
              <div className="text-[24px] text-white mb-2">
                <a href="tel:4145309114" className="hover:text-[#14B8A6] transition-colors">414-530-9114</a>
              </div>
              <div className="text-[16px] text-[#999999]">
                <a href="mailto:melissa@technelle.ai" className="hover:text-[#14B8A6] transition-colors">melissa@technelle.ai</a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-8">
                <a href="https://linkedin.com/company/technelle" aria-label="Follow Technelle on LinkedIn" className="text-[#999999] hover:text-[#14B8A6] transition-colors">
                  <i className="ri-linkedin-fill text-2xl"></i>
                </a>
                <a href="https://twitter.com/technelle" aria-label="Follow Technelle on Twitter" className="text-[#999999] hover:text-[#14B8A6] transition-colors">
                  <i className="ri-twitter-x-fill text-2xl"></i>
                </a>
              </div>
              <div className="text-[14px] text-[#999999]">© 2025 Technelle. All rights reserved.</div>
              <div className="flex items-center gap-4 flex-wrap justify-center">
                <Link to="/privacy-policy" className="text-[14px] text-[#999999] hover:text-white transition-colors">Privacy Policy</Link>
                <span className="text-[#333333]">|</span>
                <Link to="/terms" className="text-[14px] text-[#999999] hover:text-white transition-colors">Terms</Link>
                <span className="text-[#333333]">|</span>

              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
